import React, { useState } from 'react';
import { motion } from 'motion/react';
import { AnimatedCountUp } from './common/AnimatedMetricCard';
import { AthleteProfile, SportType } from '../types';
import { PlayerProgressD3Chart } from './PlayerProgressD3Chart';
import { BasketballCourtHeatmapD3 } from './BasketballCourtHeatmapD3';
import { PerformanceTrendProjectionCard } from './PerformanceTrendProjectionCard';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  Tooltip, 
  CartesianGrid, 
  BarChart, 
  Bar, 
  Legend, 
  RadarChart, 
  Radar, 
  PolarGrid, 
  PolarAngleAxis, 
  PolarRadiusAxis, 
  PieChart, 
  Pie, 
  Cell,
  ReferenceDot 
} from 'recharts';
import { BarChart3, TrendingUp, Award, Users, Filter, ArrowUpRight, Crown, Sparkles, ShieldCheck, Watch, Activity, RefreshCw, CheckCircle2, Wifi, Zap, Heart, Flame, X, Download, FileText, Target, Bot } from 'lucide-react';
import { verifyProAccess } from '../utils/authUtils';
import { ProRestrictedWrapper } from './common/ProRestrictedWrapper';

interface StatAnalyticsViewProps {
  user: AthleteProfile;
  athletes?: AthleteProfile[];
  onOpenProCheckout?: (contextMessage?: string) => void;
}

export const StatAnalyticsView: React.FC<StatAnalyticsViewProps> = ({ user, athletes = [], onOpenProCheckout }) => {
  const [selectedSport, setSelectedSport] = useState<SportType>(user.primarySport || 'basketball');
  const [compareAthleteId, setCompareAthleteId] = useState<string>('user_2');

  // Seasonal Goal State
  const [seasonalGoalTargetStat, setSeasonalGoalTargetStat] = useState<'digs' | 'kills' | 'aces' | 'blocks' | 'assists'>('digs');
  const [seasonalGoalTarget, setSeasonalGoalTarget] = useState<number>(100);
  const [isEditingGoal, setIsEditingGoal] = useState<boolean>(false);

  // Volleyball Performance Chart state (Kills vs Errors over 20 matches + League Avg comparison)
  const [compareVbLeagueAvg, setCompareVbLeagueAvg] = useState<boolean>(true);
  const [vbRadarTimeframe, setVbRadarTimeframe] = useState<'weekly' | 'monthly'>('weekly');

  // Sub-Tab & Pro History States
  const [analyticsTab, setAnalyticsTab] = useState<'all' | 'pro_analytics' | 'pro_history'>('all');
  const [proHistoryCategory, setProHistoryCategory] = useState<'pts' | 'ast' | 'reb' | 'fgPct'>('pts');
  const [showLeagueAverage, setShowLeagueAverage] = useState<boolean>(true);

  // Recharts Legend Category Toggles for Multi-Line Trajectory Overlay
  const [activeChartLines, setActiveChartLines] = useState<{
    pts: boolean;
    leagueAvgPts: boolean;
    ast: boolean;
    reb: boolean;
    fgPct: boolean;
  }>({
    pts: true,
    leagueAvgPts: true,
    ast: false,
    reb: false,
    fgPct: false,
  });

  // Selected Badge Pin Historical Popup State
  const [selectedBadgePin, setSelectedBadgePin] = useState<{
    match: string;
    badgeId: string;
    badgeName: string;
    badgeIcon: string;
    tier: string;
    xpReward: number;
    unlockedAt: string;
    description: string;
    statHighlight: string;
    pts: number;
  } | null>(null);

  // Badge Milestone Pins mapped to exact match dates on career history timeline
  const badgeMilestonePins = [
    {
      match: 'Match 3',
      pts: 22,
      badgeId: 'sharpshooter_b3',
      badgeName: '3-Point Sharpshooter',
      badgeIcon: '🎯',
      tier: 'Bronze Tier',
      xpReward: 300,
      unlockedAt: 'Match 3 • 2 weeks ago',
      description: 'Hit 4+ consecutive three-pointers in a single competitive league match.',
      statHighlight: '22 PTS • 7 AST • 51% FG',
    },
    {
      match: 'Match 5',
      pts: 26,
      badgeId: 'mvp_b5',
      badgeName: 'Playground MVP',
      badgeIcon: '⚡',
      tier: 'Silver Tier',
      xpReward: 500,
      unlockedAt: 'Match 5 • 10 days ago',
      description: 'Recorded highest efficiency rating on court in back-to-back clutch victories.',
      statHighlight: '26 PTS • 8 AST • 55% FG',
    },
    {
      match: 'Match 7',
      pts: 28,
      badgeId: 'triple_double_b7',
      badgeName: 'Triple-Double Threat',
      badgeIcon: '🏆',
      tier: 'Gold Tier',
      xpReward: 1000,
      unlockedAt: 'Match 7 • 5 days ago',
      description: 'Achieved double digits in Points, Assists, and Rebounds during rivalry game.',
      statHighlight: '28 PTS • 10 AST • 7 REB',
    },
    {
      match: 'Match 10',
      pts: 34,
      badgeId: 'immortal_b10',
      badgeName: 'Immortal GOAT',
      badgeIcon: '👑',
      tier: 'Legendary Tier',
      xpReward: 2500,
      unlockedAt: 'Match 10 • Yesterday',
      description: 'Surpassed 30+ PPG milestone while leading team to championship tournament.',
      statHighlight: '34 PTS • 12 AST • 64% FG',
    },
  ];

  // Pro Career History Mock Data
  const proCareerHistoryData = [
    { match: 'Match 1', pts: 14, ast: 4, reb: 3, fgPct: 42, avgPts: 15.2, avgAst: 4.1, avgReb: 4.0, avgFgPct: 44 },
    { match: 'Match 2', pts: 18, ast: 5, reb: 4, fgPct: 46, avgPts: 15.4, avgAst: 4.2, avgReb: 4.1, avgFgPct: 44 },
    { match: 'Match 3', pts: 22, ast: 7, reb: 5, fgPct: 51, avgPts: 15.5, avgAst: 4.2, avgReb: 4.1, avgFgPct: 45 },
    { match: 'Match 4', pts: 19, ast: 6, reb: 4, fgPct: 48, avgPts: 15.6, avgAst: 4.3, avgReb: 4.2, avgFgPct: 45 },
    { match: 'Match 5', pts: 26, ast: 8, reb: 6, fgPct: 55, avgPts: 15.8, avgAst: 4.3, avgReb: 4.2, avgFgPct: 45 },
    { match: 'Match 6', pts: 24, ast: 9, reb: 5, fgPct: 52, avgPts: 16.0, avgAst: 4.4, avgReb: 4.3, avgFgPct: 46 },
    { match: 'Match 7', pts: 28, ast: 10, reb: 7, fgPct: 58, avgPts: 16.1, avgAst: 4.4, avgReb: 4.3, avgFgPct: 46 },
    { match: 'Match 8', pts: 31, ast: 11, reb: 8, fgPct: 61, avgPts: 16.3, avgAst: 4.5, avgReb: 4.4, avgFgPct: 46 },
    { match: 'Match 9', pts: 27, ast: 9, reb: 6, fgPct: 54, avgPts: 16.4, avgAst: 4.5, avgReb: 4.4, avgFgPct: 46 },
    { match: 'Match 10', pts: 34, ast: 12, reb: 9, fgPct: 64, avgPts: 16.5, avgAst: 4.6, avgReb: 4.5, avgFgPct: 47 },
  ];

  // Download Formatted Pro Scouting Report (CSV / Summary for External Scouts)
  const handleDownloadProReport = () => {
    if (!verifyProAccess(user)) {
      if (onOpenProCheckout) onOpenProCheckout('This historical performance deep-dive requires a PRO membership to unlock.');
      return;
    }

    const ppg = (user.stats.basketball.pts / (user.stats.basketball.gamesPlayed || 1)).toFixed(1);
    const apg = (user.stats.basketball.ast / (user.stats.basketball.gamesPlayed || 1)).toFixed(1);
    const rpg = (user.stats.basketball.reb / (user.stats.basketball.gamesPlayed || 1)).toFixed(1);
    const fgPct = user.stats.basketball.fgAttempted > 0 
      ? ((user.stats.basketball.fgMade / user.stats.basketball.fgAttempted) * 100).toFixed(1)
      : '50.0';

    const reportRows = [
      ['========================================================='],
      ['PLAYGROUND PRO ATHLETE SCOUTING & PERFORMANCE REPORT'],
      ['========================================================='],
      ['Athlete Name', user?.name || 'Athlete'],
      ['Social Handle', user?.handle || '@athlete'],
      ['Primary Sport', (user?.primarySport || 'basketball').toUpperCase()],
      ['Position / Jersey', `${user?.position || 'Guard'} (#${user?.jerseyNumber || '7'})`],
      ['School / League', user?.schoolOrLeague || 'Playground League'],
      ['Verified Pro Status', user?.isPro ? 'PRO VERIFIED SUBSCRIBER 👑' : 'STANDARD ATHLETE'],
      ['Level & Title', `Level ${user?.level || 1} (${user?.levelTitle || 'Rookie'})`],
      ['Total XP', `${(user?.xp || 0).toLocaleString()} XP`],
      ['Export Date', new Date().toLocaleDateString('en-US', { dateStyle: 'full' })],
      ['---------------------------------------------------------'],
      ['CAREER METRIC BENCHMARKS'],
      ['---------------------------------------------------------'],
      ['Games Played', user.stats.basketball.gamesPlayed],
      ['Career Points', user.stats.basketball.pts],
      ['Points Per Game (PPG)', ppg],
      ['Assists Per Game (APG)', apg],
      ['Rebounds Per Game (RPG)', rpg],
      ['Field Goal Percentage', `${fgPct}%`],
      ['Win-Loss Record', `${user.winCount}W - ${user.lossCount}L`],
      ['---------------------------------------------------------'],
      ['PRO CAREER MATCH TRAJECTORY LOGS'],
      ['---------------------------------------------------------'],
      ['Match', 'Points', 'Assists', 'Rebounds', 'FG %', 'League Avg PPG'],
      ...proCareerHistoryData.map((d) => [d.match, d.pts, d.ast, d.reb, `${d.fgPct}%`, d.avgPts]),
      ['---------------------------------------------------------'],
      ['SHOT ZONE D3 EFFICIENCY BREAKDOWN'],
      ['---------------------------------------------------------'],
      ['Paint Efficiency', '68.4% (132/193 Made)'],
      ['Mid-Range Efficiency', '46.2% (78/169 Made)'],
      ['Three-Point Efficiency', '38.9% (65/167 Made)'],
      ['Free Throw Percentage', '84.2% (101/120 Made)'],
      ['---------------------------------------------------------'],
      ['SCOUT VERIFICATION AUDIT HASH'],
      ['SHA-256 Verification Hash', `0xPRO_${Math.random().toString(36).substring(2, 10).toUpperCase()}_VERIFIED_SCOUT`],
      ['========================================================='],
    ];

    const csvContent = 'data:text/csv;charset=utf-8,' + reportRows.map((e) => e.map(val => `"${val}"`).join(',')).join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${(user?.name || 'athlete').toLowerCase().replace(/\s+/g, '_')}_pro_scouting_report.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Wearable Integration State
  const [isSyncingWearable, setIsSyncingWearable] = useState(false);
  const [wearableData, setWearableData] = useState<{
    device: string;
    heartRate: number;
    calories: number;
    activeMinutes: number;
    strain: number;
    syncedAt: string;
  } | null>(null);
  const [syncToast, setSyncToast] = useState<string | null>(null);

  const handleSyncWearable = () => {
    if (!verifyProAccess(user)) {
      if (onOpenProCheckout) onOpenProCheckout('This historical performance deep-dive requires a PRO membership to unlock.');
      return;
    }

    setIsSyncingWearable(true);
    setSyncToast('Connecting to Apple Watch / WHOOP / Garmin via Bluetooth LE...');

    setTimeout(() => {
      const randomHR = Math.floor(Math.random() * 25) + 145; // 145 - 170 bpm
      const randomCals = Math.floor(Math.random() * 200) + 650; // 650 - 850 kcal
      const randomMins = Math.floor(Math.random() * 30) + 50; // 50 - 80 mins
      const randomStrain = Number((Math.random() * 3.5 + 14.5).toFixed(1)); // 14.5 - 18.0
      const devices = ['Apple Watch Ultra 2', 'WHOOP Strap 4.0', 'Garmin Forerunner 965', 'Fitbit Sense 2'];
      const chosenDevice = devices[Math.floor(Math.random() * devices.length)];
      const nowTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      setWearableData({
        device: chosenDevice,
        heartRate: randomHR,
        calories: randomCals,
        activeMinutes: randomMins,
        strain: randomStrain,
        syncedAt: nowTime,
      });

      setIsSyncingWearable(false);
      setSyncToast(`Synced with ${chosenDevice}! Pulled ${randomHR} BPM & ${randomCals} kcal 🔥`);

      setTimeout(() => {
        setSyncToast(null);
      }, 4000);
    }, 1600);
  };

  const compareAthlete = athletes.find((a) => a.id === compareAthleteId) || user;

  // Game log trend data
  const basketballGameLogs = [
    { game: 'Game 1', pts: 18, ast: 6, reb: 4 },
    { game: 'Game 2', pts: 24, ast: 8, reb: 3 },
    { game: 'Game 3', pts: 15, ast: 5, reb: 6 },
    { game: 'Game 4', pts: 28, ast: 11, reb: 5 },
    { game: 'Game 5', pts: 22, ast: 7, reb: 4 },
    { game: 'Game 6', pts: 31, ast: 9, reb: 7 },
  ];

  const baseballGameLogs = [
    { game: 'Game 1', hits: 2, rbis: 3, atBats: 4 },
    { game: 'Game 2', hits: 1, rbis: 0, atBats: 3 },
    { game: 'Game 3', hits: 3, rbis: 4, atBats: 4 },
    { game: 'Game 4', hits: 2, rbis: 1, atBats: 3 },
    { game: 'Game 5', hits: 0, rbis: 0, atBats: 3 },
    { game: 'Game 6', hits: 4, rbis: 5, atBats: 5 },
  ];

  const softballGameLogs = [
    { game: 'Game 1', hits: 3, rbis: 2, stolenBases: 1 },
    { game: 'Game 2', hits: 2, rbis: 4, stolenBases: 2 },
    { game: 'Game 3', hits: 1, rbis: 1, stolenBases: 0 },
    { game: 'Game 4', hits: 4, rbis: 3, stolenBases: 3 },
    { game: 'Game 5', hits: 2, rbis: 2, stolenBases: 1 },
    { game: 'Game 6', hits: 3, rbis: 4, stolenBases: 2 },
  ];

  const pickleballGameLogs = [
    { game: 'Match 1', dinks: 24, aces: 6 },
    { game: 'Match 2', dinks: 32, aces: 8 },
    { game: 'Match 3', dinks: 18, aces: 4 },
    { game: 'Match 4', dinks: 40, aces: 11 },
    { game: 'Match 5', dinks: 28, aces: 7 },
    { game: 'Match 6', dinks: 35, aces: 9 },
  ];

  const footballGameLogs = [
    { game: 'Week 1', passingYards: 210, touchdowns: 3 },
    { game: 'Week 2', passingYards: 280, touchdowns: 4 },
    { game: 'Week 3', passingYards: 190, touchdowns: 2 },
    { game: 'Week 4', passingYards: 310, touchdowns: 4 },
    { game: 'Week 5', passingYards: 260, touchdowns: 3 },
  ];

  const tennisGameLogs = [
    { game: 'Match 1', aces: 8, breakPointsWon: 4 },
    { game: 'Match 2', aces: 12, breakPointsWon: 6 },
    { game: 'Match 3', aces: 6, breakPointsWon: 3 },
    { game: 'Match 4', aces: 14, breakPointsWon: 7 },
  ];

  const volleyballGameLogs = [
    { game: 'Match 1', kills: 12, digs: 15, aces: 4, blocks: 3 },
    { game: 'Match 2', kills: 14, digs: 18, aces: 5, blocks: 2 },
    { game: 'Match 3', kills: 10, digs: 12, aces: 3, blocks: 4 },
    { game: 'Match 4', kills: 16, digs: 20, aces: 6, blocks: 5 },
    { game: 'Match 5', kills: 15, digs: 22, aces: 4, blocks: 3 },
    { game: 'Match 6', kills: 18, digs: 25, aces: 7, blocks: 6 },
    { game: 'Match 7', kills: 13, digs: 16, aces: 5, blocks: 3 },
    { game: 'Match 8', kills: 19, digs: 24, aces: 8, blocks: 4 },
  ];

  // Radar chart metrics comparison
  const bGamesA = user.stats.basketball.gamesPlayed || 0;
  const b3AttA = user.stats.basketball.threeAttempted || 0;
  const bFgAttA = user.stats.basketball.fgAttempted || 0;

  const bGamesB = compareAthlete.stats.basketball.gamesPlayed || 0;
  const b3AttB = compareAthlete.stats.basketball.threeAttempted || 0;
  const bFgAttB = compareAthlete.stats.basketball.fgAttempted || 0;

  const basketballRadarData = [
    { 
      subject: 'Scoring (PPG)', 
      A: bGamesA > 0 ? Number((user.stats.basketball.pts / bGamesA).toFixed(1)) : 0, 
      B: bGamesB > 0 ? Number((compareAthlete.stats.basketball.pts / bGamesB).toFixed(1)) : 0, 
      fullMark: 30 
    },
    { 
      subject: 'Assists', 
      A: bGamesA > 0 ? Number((user.stats.basketball.ast / bGamesA).toFixed(1)) : 0, 
      B: bGamesB > 0 ? Number((compareAthlete.stats.basketball.ast / bGamesB).toFixed(1)) : 0, 
      fullMark: 10 
    },
    { 
      subject: 'Rebounds', 
      A: bGamesA > 0 ? Number((user.stats.basketball.reb / bGamesA).toFixed(1)) : 0, 
      B: bGamesB > 0 ? Number((compareAthlete.stats.basketball.reb / bGamesB).toFixed(1)) : 0, 
      fullMark: 10 
    },
    { 
      subject: '3PT %', 
      A: b3AttA > 0 ? Math.round((user.stats.basketball.threeMade / b3AttA) * 100) : 0, 
      B: b3AttB > 0 ? Math.round((compareAthlete.stats.basketball.threeMade / b3AttB) * 100) : 0, 
      fullMark: 100 
    },
    { 
      subject: 'FG %', 
      A: bFgAttA > 0 ? Math.round((user.stats.basketball.fgMade / bFgAttA) * 100) : 0, 
      B: bFgAttB > 0 ? Math.round((compareAthlete.stats.basketball.fgMade / bFgAttB) * 100) : 0, 
      fullMark: 100 
    },
    { 
      subject: 'Steals', 
      A: bGamesA > 0 ? Number((user.stats.basketball.stl / bGamesA).toFixed(1)) : 0, 
      B: bGamesB > 0 ? Number((compareAthlete.stats.basketball.stl / bGamesB).toFixed(1)) : 0, 
      fullMark: 5 
    },
  ];

  // Win loss donut data
  const totalGamesOutcome = (user.winCount || 0) + (user.lossCount || 0);
  const winRatePercentage = totalGamesOutcome > 0 ? Math.round(((user.winCount || 0) / totalGamesOutcome) * 100) : 0;

  const winLossData = totalGamesOutcome > 0 
    ? [
        { name: 'Wins', value: user.winCount || 0, color: '#A3E635' },
        { name: 'Losses', value: user.lossCount || 0, color: '#F43F5E' },
      ]
    : [
        { name: 'No Games Yet', value: 1, color: '#312E81' },
      ];

  return (
    <div className="space-y-6 pb-20 lg:pb-8 animate-fadeIn text-white">
      {/* Toast Notification for Wearable Sync */}
      {syncToast && (
        <div className="p-4 bg-indigo-900 border-2 border-lime-400 text-white font-bold text-xs rounded-2xl shadow-2xl flex items-center justify-between animate-fadeIn">
          <div className="flex items-center space-x-2">
            <Watch className={`w-5 h-5 text-lime-400 shrink-0 ${isSyncingWearable ? 'animate-spin' : 'animate-bounce'}`} />
            <span>{syncToast}</span>
          </div>
          <button
            type="button"
            onClick={() => setSyncToast(null)}
            className="p-1 hover:bg-white/10 rounded-lg transition"
          >
            <X className="w-4 h-4 text-indigo-300" />
          </button>
        </div>
      )}

      {/* Non-Pro Upgrade Alert Banner */}
      {!verifyProAccess(user) && (
        <div className="bg-gradient-to-r from-amber-950/80 via-indigo-950 to-amber-950/80 p-5 rounded-[2rem] border-2 border-amber-400/50 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-amber-400 text-black rounded-2xl shadow-lg shrink-0">
              <Crown className="w-6 h-6 fill-black" />
            </div>
            <div>
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-amber-400/20 text-amber-300 rounded font-mono border border-amber-400/40">
                PRO ANALYTICS RESTRICTED
              </span>
              <h3 className="text-sm font-black italic uppercase text-white mt-1">
                Unlock Full D3 Analytics, Shot Heatmaps & Wearable Sync
              </h3>
              <p className="text-xs text-indigo-200/80">
                Upgrade to PRO to access deep shot chart overlays, D3 career projections, and scout reports.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => onOpenProCheckout && onOpenProCheckout('This historical performance deep-dive requires a PRO membership to unlock.')}
            className="px-6 py-3 bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-xs rounded-xl shadow-xl shadow-amber-400/20 transition shrink-0 flex items-center space-x-1.5"
          >
            <Crown className="w-4 h-4 fill-black" />
            <span>Upgrade to PRO ($9.99/mo)</span>
          </button>
        </div>
      )}

      {/* Top Header & Filter */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <BarChart3 className="w-6 h-6 text-lime-400" />
            <h1 className="text-2xl font-black italic uppercase tracking-tight text-white flex items-center gap-2">
              <span>Performance Analytics & Stats</span>
              {verifyProAccess(user) && (
                <span className="px-2.5 py-0.5 rounded-full bg-amber-400 text-black font-black text-[10px] font-mono flex items-center gap-1 shadow">
                  <Crown className="w-3 h-3 fill-black" />
                  <span>PRO UNLOCKED</span>
                </span>
              )}
            </h1>
          </div>
          <p className="text-xs text-indigo-200/70 mt-1 font-semibold">
            Real-time scoring trends, shooting percentages, and athlete comparisons.
          </p>
        </div>

        {/* Sync with Wearable Action Button & Sport Switcher */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
          <button
            type="button"
            onClick={handleSyncWearable}
            disabled={isSyncingWearable}
            className={`px-4 py-2.5 rounded-2xl font-black italic text-xs uppercase transition flex items-center space-x-2 shadow-lg border ${
              isSyncingWearable
                ? 'bg-indigo-950 border-lime-400 text-lime-400 animate-pulse'
                : 'bg-lime-400 hover:bg-lime-300 text-black border-lime-300 shadow-lime-400/20 active:scale-95'
            }`}
          >
            <Watch className={`w-4 h-4 ${isSyncingWearable ? 'animate-spin' : ''}`} />
            <span>{isSyncingWearable ? 'Syncing Device...' : 'Sync with Wearable'}</span>
            <Zap className="w-3.5 h-3.5 text-black fill-black" />
          </button>

          {/* Sport Switcher */}
          <div className="flex flex-wrap items-center bg-indigo-950 p-1.5 rounded-2xl border border-white/10 gap-1">
          <button
            onClick={() => setSelectedSport('basketball')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'basketball'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Basketball 🏀
          </button>
          <button
            onClick={() => setSelectedSport('baseball')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'baseball'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Baseball ⚾
          </button>
          <button
            onClick={() => setSelectedSport('softball')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'softball'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Softball 🥎
          </button>
          <button
            onClick={() => setSelectedSport('pickleball')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'pickleball'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Pickleball 🏓
          </button>
          <button
            onClick={() => setSelectedSport('soccer')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'soccer'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Soccer ⚽
          </button>
          <button
            onClick={() => setSelectedSport('football')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'football'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Football 🏈
          </button>
          <button
            onClick={() => setSelectedSport('tennis')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'tennis'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Tennis 🎾
          </button>
          <button
            onClick={() => setSelectedSport('volleyball')}
            className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
              selectedSport === 'volleyball'
                ? 'bg-lime-400 text-black shadow-md'
                : 'text-indigo-200/60 hover:text-white'
            }`}
          >
            Volleyball 🏐
          </button>
        </div>
      </div>
    </div>

      {/* SEASONAL GOAL TARGET & PROGRESS TRACKER */}
      <div className="bg-gradient-to-r from-indigo-900/90 via-purple-950/80 to-indigo-950 p-6 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-4 relative overflow-hidden">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg">
              <Target className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-lime-400/20 text-lime-300 rounded font-mono border border-lime-400/40">
                  SEASONAL TARGET GOAL
                </span>
                <span className="text-xs text-indigo-200/80 font-bold">Volleyball Tracker 🏐</span>
              </div>
              <h3 className="text-xl font-black italic uppercase text-white mt-1">
                {seasonalGoalTargetStat.toUpperCase()} TARGET: {seasonalGoalTarget} THIS SEASON
              </h3>
            </div>
          </div>

          <button
            type="button"
            onClick={() => setIsEditingGoal(!isEditingGoal)}
            className="px-4 py-2 bg-indigo-950 hover:bg-indigo-900 text-lime-400 border border-lime-400/40 font-black italic uppercase text-xs rounded-xl shadow-md transition flex items-center space-x-1.5 shrink-0"
          >
            <Zap className="w-3.5 h-3.5 fill-lime-400" />
            <span>{isEditingGoal ? 'Close Goal Editor' : 'Edit Goal Target'}</span>
          </button>
        </div>

        {/* Goal Edit Drawer Form */}
        {isEditingGoal && (
          <div className="p-4 bg-indigo-950/90 rounded-2xl border border-lime-400/30 space-y-3 animate-fadeIn text-xs">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="text-indigo-200/80 font-bold block mb-1">Target Volleyball Stat</label>
                <select
                  value={seasonalGoalTargetStat}
                  onChange={(e) => setSeasonalGoalTargetStat(e.target.value as any)}
                  className="w-full bg-indigo-900 border border-white/20 rounded-xl px-3 py-2 text-white font-bold"
                >
                  <option value="digs">Digs 🛡️</option>
                  <option value="kills">Spike Kills 🏐</option>
                  <option value="aces">Service Aces ⚡</option>
                  <option value="blocks">Net Blocks 🧱</option>
                  <option value="assists">Setting Assists 🎯</option>
                </select>
              </div>

              <div>
                <label className="text-indigo-200/80 font-bold block mb-1">Seasonal Numerical Target</label>
                <input
                  type="number"
                  value={seasonalGoalTarget}
                  onChange={(e) => setSeasonalGoalTarget(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-full bg-indigo-900 border border-white/20 rounded-xl px-3 py-2 text-white font-bold font-mono"
                />
              </div>

              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => setIsEditingGoal(false)}
                  className="w-full py-2 bg-lime-400 text-black font-black italic uppercase rounded-xl hover:bg-lime-300 transition"
                >
                  Save New Goal Target
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar & Numerical Metrics */}
        {(() => {
          const currentVal = user.stats?.volleyball?.[seasonalGoalTargetStat] || 0;
          const progressPct = Math.min(100, Math.round((currentVal / (seasonalGoalTarget || 1)) * 100));
          return (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="text-indigo-200 flex items-center space-x-2">
                  <span>Current Progress:</span>
                  <span className="text-lime-400 font-extrabold text-sm">{currentVal}</span>
                  <span className="text-indigo-300/70">/ {seasonalGoalTarget} {seasonalGoalTargetStat}</span>
                </span>
                <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black font-mono border ${
                  progressPct >= 100 
                    ? 'bg-emerald-400 text-black border-emerald-300 shadow-md' 
                    : 'bg-lime-400/20 text-lime-300 border-lime-400/40'
                }`}>
                  {progressPct >= 100 ? '🎉 GOAL ACHIEVED!' : `${progressPct}% ON TRACK`}
                </span>
              </div>

              {/* Progress Bar Track */}
              <div className="w-full h-4 bg-indigo-950 rounded-full border border-white/10 p-0.5 overflow-hidden shadow-inner">
                <div
                  className="h-full bg-gradient-to-r from-lime-400 via-amber-400 to-emerald-400 rounded-full transition-all duration-700 ease-out shadow-[0_0_12px_rgba(163,230,53,0.6)]"
                  style={{ width: `${progressPct}%` }}
                />
              </div>

              <div className="flex items-center justify-between text-[11px] text-indigo-300/80 font-mono pt-1">
                <span>Logged from Volleyball Matches</span>
                <span>{Math.max(0, seasonalGoalTarget - currentVal)} remaining to reach goal</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Sub-Tabs: Overview vs PRO Analytics vs PRO Stat History */}
      <div className="flex flex-wrap items-center gap-2 bg-indigo-900/60 p-2 rounded-2xl border border-white/10">
        <button
          onClick={() => setAnalyticsTab('all')}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 ${
            analyticsTab === 'all'
              ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
              : 'text-indigo-200 hover:text-white bg-indigo-950 border border-white/10'
          }`}
        >
          <BarChart3 className="w-4 h-4" />
          <span>All Overview & Telemetry</span>
        </button>

        <button
          onClick={() => {
            if (!verifyProAccess(user)) {
              if (onOpenProCheckout) onOpenProCheckout('This historical performance deep-dive requires a PRO membership to unlock.');
            } else {
              setAnalyticsTab('pro_analytics');
            }
          }}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 ${
            analyticsTab === 'pro_analytics'
              ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black shadow-lg shadow-amber-400/20'
              : 'text-indigo-200 hover:text-white bg-indigo-950 border border-white/10'
          }`}
        >
          <Sparkles className="w-4 h-4 text-amber-400" />
          <span>PRO Analytics & AI Projections</span>
          <span className="bg-amber-400/20 text-amber-300 text-[9px] font-black px-1.5 py-0.5 rounded font-mono border border-amber-400/40">
            PRO 👑
          </span>
        </button>

        <button
          onClick={() => {
            if (!verifyProAccess(user)) {
              if (onOpenProCheckout) onOpenProCheckout('This historical performance deep-dive requires a PRO membership to unlock.');
            } else {
              setAnalyticsTab('pro_history');
            }
          }}
          className={`px-4 py-2 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 ${
            analyticsTab === 'pro_history'
              ? 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black shadow-lg shadow-amber-400/20'
              : 'text-indigo-200 hover:text-white bg-indigo-950 border border-white/10'
          }`}
        >
          <TrendingUp className="w-4 h-4 text-amber-400" />
          <span>Pro Stat History Chart</span>
          <span className="bg-amber-400/20 text-amber-300 text-[9px] font-black px-1.5 py-0.5 rounded font-mono border border-amber-400/40">
            D3 / Recharts
          </span>
        </button>
      </div>

      {/* PRO STAT HISTORY & CATEGORY OVERLAY CHART (Recharts) */}
      {(analyticsTab === 'pro_history' || analyticsTab === 'pro_analytics' || analyticsTab === 'all') && (
        <ProRestrictedWrapper
          user={user}
          onOpenProCheckout={onOpenProCheckout}
          featureTitle="PRO Career Stat History & AI Scout Analysis"
          featureDescription="Unlock 10-match PPG trajectory overlay charts, D3 milestone pins, and AI Scout intelligence breakdowns."
          upsellMessage="This historical performance deep-dive requires a PRO membership to unlock."
        >
          <div className="bg-gradient-to-br from-indigo-900/90 via-indigo-950 to-amber-950/40 p-6 rounded-[2.5rem] border-2 border-amber-400/50 shadow-2xl space-y-6">
            {/* AI SCOUT INTELLIGENCE SUMMARY BOX */}
          <div className="p-5 bg-indigo-950/90 rounded-2xl border-2 border-amber-400/40 shadow-xl space-y-3 relative overflow-hidden">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-3">
              <div className="flex items-center space-x-2.5">
                <div className="p-2.5 bg-gradient-to-r from-amber-400 to-yellow-300 text-black rounded-xl shadow-md">
                  <Bot className="w-5 h-5 stroke-[2.5]" />
                </div>
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-black uppercase font-mono px-2 py-0.5 rounded bg-amber-400/20 text-amber-300 border border-amber-400/30">
                      PRO SCOUT AI ENGINE ⚡
                    </span>
                    <span className="text-[10px] text-indigo-300/80 font-mono">
                      Analyzed {proCareerHistoryData.length} Recent Matches
                    </span>
                  </div>
                  <h3 className="text-base font-black italic uppercase text-white mt-0.5 flex items-center gap-2">
                    <span>AI Scout Intelligence Summary</span>
                    <Sparkles className="w-4 h-4 text-amber-400 fill-amber-400/20" />
                  </h3>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <span className="px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[11px] font-bold font-mono border border-emerald-500/30 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  Scout Evaluation Verified
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
              {/* Area of Growth */}
              <div className="p-3.5 bg-indigo-900/60 rounded-xl border border-white/10 space-y-1.5">
                <div className="flex items-center space-x-1.5 text-xs font-black italic uppercase text-amber-300 font-mono">
                  <TrendingUp className="w-4 h-4 text-amber-400" />
                  <span>Biggest Area of Growth</span>
                </div>
                <p className="text-xs text-indigo-100 leading-relaxed font-sans">
                  <strong className="text-amber-300 font-bold">Scoring Efficiency & Playmaking Surge:</strong> Over the last 10 matches, scoring increased by <span className="text-lime-400 font-bold">+142.8%</span> (14 ➔ 34 PTS/game) while boosting FG efficiency from 42% to <span className="text-lime-400 font-bold">64%</span>. Playmaking peaked at <span className="text-amber-300 font-bold">12.0 APG</span>, outperforming league average benchmark by +17.5 PPG.
                </p>
              </div>

              {/* Focus Area for Next 3 Matches */}
              <div className="p-3.5 bg-indigo-900/60 rounded-xl border border-white/10 space-y-1.5">
                <div className="flex items-center space-x-1.5 text-xs font-black italic uppercase text-lime-300 font-mono">
                  <Target className="w-4 h-4 text-lime-400" />
                  <span>Focus Area (Next 3 Matches)</span>
                </div>
                <p className="text-xs text-indigo-100 leading-relaxed font-sans">
                  <strong className="text-lime-300 font-bold">Defensive Glass & Transition Security:</strong> Maintain a minimum baseline of <span className="text-lime-300 font-bold">10.0 RPG</span> to lock in double-double scout metrics, while minimizing contested mid-range pullups during early fast breaks to sustain 60%+ FG shooting.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-white/10 pb-4">
            <div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-0.5 rounded bg-amber-400 text-black text-[10px] font-black uppercase font-mono">
                  PRO SUBSCRIBER EXCLUSIVE 👑
                </span>
                <span className="text-xs text-amber-300 font-bold">Career Trajectory vs. League Benchmarks</span>
              </div>
              <h2 className="text-xl font-black italic uppercase text-white mt-1 flex items-center gap-2">
                <span>Multi-Category PPG & Trajectory Overlay</span>
              </h2>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Download Pro Report Button */}
              <button
                type="button"
                onClick={handleDownloadProReport}
                className="px-4 py-2 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-amber-400/20 flex items-center space-x-2 transition transform hover:-translate-y-0.5 shrink-0"
              >
                <Download className="w-4 h-4 stroke-[2.5]" />
                <span>Download Pro Report</span>
                <span className="bg-black/10 px-1.5 py-0.5 rounded text-[9px] font-mono font-bold">CSV</span>
              </button>

              {/* Interactive Legend Category Line Toggles */}
              <div className="flex flex-wrap items-center gap-2">
              <span className="text-[10px] font-mono text-indigo-300 font-bold uppercase mr-1">Legend Toggles:</span>
              <button
                type="button"
                onClick={() => setActiveChartLines((prev) => ({ ...prev, pts: !prev.pts }))}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border ${
                  activeChartLines.pts
                    ? 'bg-amber-400 text-black border-amber-300 shadow-lg shadow-amber-400/20'
                    : 'bg-indigo-950 text-indigo-400 border-white/10 opacity-60'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block"></span>
                <span>My PPG Trajectory</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveChartLines((prev) => ({ ...prev, leagueAvgPts: !prev.leagueAvgPts }))}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border ${
                  activeChartLines.leagueAvgPts
                    ? 'bg-slate-300 text-black border-slate-200 shadow-lg'
                    : 'bg-indigo-950 text-indigo-400 border-white/10 opacity-60'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 inline-block border border-dashed border-slate-600"></span>
                <span>League Avg Benchmark</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveChartLines((prev) => ({ ...prev, ast: !prev.ast }))}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border ${
                  activeChartLines.ast
                    ? 'bg-blue-500 text-white border-blue-400 shadow-lg'
                    : 'bg-indigo-950 text-indigo-400 border-white/10 opacity-60'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-blue-400 inline-block"></span>
                <span>My Assists (APG)</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveChartLines((prev) => ({ ...prev, reb: !prev.reb }))}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border ${
                  activeChartLines.reb
                    ? 'bg-emerald-500 text-white border-emerald-400 shadow-lg'
                    : 'bg-indigo-950 text-indigo-400 border-white/10 opacity-60'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 inline-block"></span>
                <span>My Rebounds (RPG)</span>
              </button>

              <button
                type="button"
                onClick={() => setActiveChartLines((prev) => ({ ...prev, fgPct: !prev.fgPct }))}
                className={`px-3 py-1 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 border ${
                  activeChartLines.fgPct
                    ? 'bg-pink-500 text-white border-pink-400 shadow-lg'
                    : 'bg-indigo-950 text-indigo-400 border-white/10 opacity-60'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-full bg-pink-400 inline-block"></span>
                <span>Field Goal %</span>
              </button>
            </div>
          </div>
        </div>

          {/* Recharts Pro Stat History Line Chart with Multi-Line Overlay & Badge Milestone Pins */}
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={proCareerHistoryData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#312E81" opacity={0.3} />
                <XAxis dataKey="match" stroke="#A5B4FC" fontSize={12} />
                <YAxis stroke="#A5B4FC" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#0B0F2A',
                    borderColor: '#F59E0B',
                    borderRadius: '16px',
                    color: '#fff',
                    boxShadow: '0 0 20px rgba(0,0,0,0.8)',
                  }}
                />
                <Legend />
                {activeChartLines.pts && (
                  <Line
                    type="monotone"
                    dataKey="pts"
                    name="My Points-Per-Game Trajectory"
                    stroke="#F59E0B"
                    strokeWidth={4}
                    dot={{ r: 6, fill: '#F59E0B' }}
                    activeDot={{ r: 9, fill: '#FCD34D' }}
                  />
                )}
                {activeChartLines.leagueAvgPts && (
                  <Line
                    type="monotone"
                    dataKey="avgPts"
                    name="League Average Benchmark"
                    stroke="#94A3B8"
                    strokeWidth={2.5}
                    strokeDasharray="6 6"
                    dot={{ r: 4, fill: '#94A3B8' }}
                  />
                )}
                {activeChartLines.ast && (
                  <Line
                    type="monotone"
                    dataKey="ast"
                    name="My Assists / Game"
                    stroke="#3B82F6"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#3B82F6' }}
                  />
                )}
                {activeChartLines.reb && (
                  <Line
                    type="monotone"
                    dataKey="reb"
                    name="My Rebounds / Game"
                    stroke="#10B981"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#10B981' }}
                  />
                )}
                {activeChartLines.fgPct && (
                  <Line
                    type="monotone"
                    dataKey="fgPct"
                    name="My Field Goal %"
                    stroke="#EC4899"
                    strokeWidth={3}
                    dot={{ r: 5, fill: '#EC4899' }}
                  />
                )}

                {/* Interactive Major Badge Milestone Reference Dot Pins on Chart Timeline */}
                {badgeMilestonePins.map((pin) => (
                  <ReferenceDot
                    key={pin.badgeId}
                    x={pin.match}
                    y={pin.pts}
                    r={14}
                    fill="#0B0F2A"
                    stroke="#F59E0B"
                    strokeWidth={2.5}
                    className="cursor-pointer"
                    shape={(props: any) => {
                      const { cx, cy } = props;
                      if (!cx || !cy) return null;
                      return (
                        <g
                          transform={`translate(${cx}, ${cy})`}
                          className="cursor-pointer group"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedBadgePin(pin);
                          }}
                        >
                          <circle r="16" fill="#F59E0B" opacity="0.35" className="animate-ping" />
                          <circle r="13" fill="#0B0F2A" stroke="#F59E0B" strokeWidth="2.5" />
                          <text x="0" y="4" textAnchor="middle" fontSize="12" fill="#FFF">
                            {pin.badgeIcon}
                          </text>
                        </g>
                      );
                    }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Interactive Timeline Badge Pins Strip */}
          <div className="pt-3 border-t border-white/10 space-y-2">
            <div className="flex items-center justify-between text-xs font-mono">
              <span className="text-amber-300 font-black uppercase italic flex items-center gap-1.5">
                <Crown className="w-3.5 h-3.5 text-amber-400" />
                <span>Historical Major Badge Unlock Timeline Pins:</span>
              </span>
              <span className="text-[10px] text-indigo-300/80">Click pin on chart or badge chip to inspect historical stats & XP</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {badgeMilestonePins.map((pin) => (
                <button
                  key={pin.badgeId}
                  type="button"
                  onClick={() => setSelectedBadgePin(pin)}
                  className="px-3 py-1.5 bg-indigo-950 hover:bg-amber-950/60 border border-amber-400/40 hover:border-amber-400 rounded-xl text-xs text-white font-bold flex items-center space-x-2 transition shadow-md group"
                >
                  <span className="text-sm group-hover:scale-125 transition-transform">{pin.badgeIcon}</span>
                  <span className="font-mono text-amber-300 font-extrabold">{pin.match}:</span>
                  <span>{pin.badgeName}</span>
                  <span className="px-1.5 py-0.5 rounded bg-amber-400 text-black font-black text-[9px] font-mono">
                    +{pin.xpReward} XP
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
        </ProRestrictedWrapper>
      )}

      {/* Wearable Biometrics Telemetry Card */}
      {wearableData ? (
        <div className="bg-gradient-to-r from-indigo-950 via-indigo-900 to-indigo-950 p-6 rounded-[2.5rem] border-2 border-lime-400/50 shadow-2xl space-y-4 animate-fadeIn relative overflow-hidden">
          <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
            <Watch className="w-36 h-36 text-lime-400" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 relative z-10 border-b border-white/10 pb-3">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg ring-2 ring-lime-300">
                <Watch className="w-6 h-6 stroke-[2.5]" />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-black uppercase tracking-wider flex items-center space-x-1 border border-emerald-500/30">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                    <span>Live Synced</span>
                  </span>
                  <span className="text-[10px] text-indigo-300/80 font-mono">
                    Synced at {wearableData.syncedAt}
                  </span>
                </div>
                <h3 className="text-lg font-black italic uppercase text-white mt-0.5 flex items-center gap-2">
                  <span>{wearableData.device} Telemetry</span>
                  <Wifi className="w-4 h-4 text-lime-400 animate-pulse" />
                </h3>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSyncWearable}
              disabled={isSyncingWearable}
              className="px-3 py-1.5 rounded-xl bg-indigo-900 hover:bg-indigo-800 border border-white/10 text-indigo-200 text-xs font-extrabold flex items-center space-x-1.5 transition self-start sm:self-auto"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isSyncingWearable ? 'animate-spin' : ''}`} />
              <span>Refresh Telemetry</span>
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 relative z-10">
            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-1">
              <div className="flex items-center justify-between text-indigo-300 text-xs font-bold">
                <span className="flex items-center space-x-1">
                  <Heart className="w-4 h-4 text-rose-500 animate-pulse fill-rose-500/30" />
                  <span>Heart Rate</span>
                </span>
                <span className="text-[10px] text-rose-400 font-mono">BPM</span>
              </div>
              <div className="text-2xl font-black font-mono text-white">
                {wearableData.heartRate} <span className="text-xs text-indigo-300 font-normal">avg</span>
              </div>
              <div className="w-full bg-indigo-900 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-rose-500 h-full rounded-full"
                  style={{ width: `${Math.min(100, (wearableData.heartRate / 190) * 100)}%` }}
                />
              </div>
            </div>

            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-1">
              <div className="flex items-center justify-between text-indigo-300 text-xs font-bold">
                <span className="flex items-center space-x-1">
                  <Flame className="w-4 h-4 text-amber-400 fill-amber-400/30" />
                  <span>Active Burn</span>
                </span>
                <span className="text-[10px] text-amber-400 font-mono">KCAL</span>
              </div>
              <div className="text-2xl font-black font-mono text-white">
                {wearableData.calories} <span className="text-xs text-indigo-300 font-normal">kcal</span>
              </div>
              <div className="w-full bg-indigo-900 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-amber-400 h-full rounded-full"
                  style={{ width: `${Math.min(100, (wearableData.calories / 1000) * 100)}%` }}
                />
              </div>
            </div>

            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-1">
              <div className="flex items-center justify-between text-indigo-300 text-xs font-bold">
                <span className="flex items-center space-x-1">
                  <Activity className="w-4 h-4 text-lime-400" />
                  <span>Active Time</span>
                </span>
                <span className="text-[10px] text-lime-400 font-mono">MIN</span>
              </div>
              <div className="text-2xl font-black font-mono text-white">
                {wearableData.activeMinutes} <span className="text-xs text-indigo-300 font-normal">mins</span>
              </div>
              <div className="w-full bg-indigo-900 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-lime-400 h-full rounded-full"
                  style={{ width: `${Math.min(100, (wearableData.activeMinutes / 90) * 100)}%` }}
                />
              </div>
            </div>

            <div className="bg-indigo-950/80 p-4 rounded-2xl border border-white/10 space-y-1">
              <div className="flex items-center justify-between text-indigo-300 text-xs font-bold">
                <span className="flex items-center space-x-1">
                  <Zap className="w-4 h-4 text-indigo-300 fill-indigo-300/30" />
                  <span>Strain Score</span>
                </span>
                <span className="text-[10px] text-indigo-300 font-mono">WHOOP / 21</span>
              </div>
              <div className="text-2xl font-black font-mono text-white">
                {wearableData.strain} <span className="text-xs text-indigo-300 font-normal">/ 21</span>
              </div>
              <div className="w-full bg-indigo-900 h-1.5 rounded-full overflow-hidden">
                <div
                  className="bg-indigo-400 h-full rounded-full"
                  style={{ width: `${(wearableData.strain / 21) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-indigo-950/60 p-5 rounded-[2.5rem] border border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-indigo-900 text-indigo-300 rounded-2xl border border-white/10">
              <Watch className="w-6 h-6" />
            </div>
            <div>
              <h4 className="font-extrabold text-sm text-white flex items-center gap-2">
                <span>Connect Apple Watch, Garmin, WHOOP, or Fitbit</span>
                <span className="px-2 py-0.5 rounded-md bg-indigo-900 text-indigo-300 text-[10px] font-mono">
                  BLUETOOTH LE
                </span>
              </h4>
              <p className="text-xs text-indigo-200/70">
                Sync live heart rate zones, calories burned, and strain metrics to auto-populate game analytics.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSyncWearable}
            disabled={isSyncingWearable}
            className="px-4 py-2 bg-indigo-900 hover:bg-lime-400 hover:text-black border border-white/10 text-white font-black italic text-xs uppercase rounded-2xl transition flex items-center space-x-1.5 shrink-0"
          >
            <Watch className="w-4 h-4" />
            <span>Sync Wearable Tracker</span>
          </button>
        </div>
      )}

      {/* Performance Trend Projection AI Card */}
      <PerformanceTrendProjectionCard user={user} selectedSport={selectedSport} onOpenProCheckout={onOpenProCheckout} />

      {/* Shot Success Rate by Court Zone Visualization (Recharts) */}
      {(analyticsTab === 'pro_analytics' || analyticsTab === 'all') && (
        <ShotZoneSuccessChart user={user} onOpenProCheckout={onOpenProCheckout} />
      )}

      {/* D3 30-Day Player Progress Trajectory Chart */}
      <PlayerProgressD3Chart user={user} selectedSport={selectedSport} onOpenProCheckout={onOpenProCheckout} />

      {/* D3 Basketball Court Position Preference Heatmap */}
      <BasketballCourtHeatmapD3 user={user} onOpenProCheckout={onOpenProCheckout} />

      {/* Row 1: Game Trend Chart + Win Rate Donut */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Scoring Trend Line Chart (2 cols) */}
        <div className="lg:col-span-2 bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-black italic uppercase text-white text-base">
                Game-by-Game Scoring & Production Trend
              </h3>
              <p className="text-xs text-indigo-200/70">
                Stat trajectory over your last 6 games played
              </p>
            </div>
            <span className="text-xs font-black italic uppercase px-3 py-1 bg-lime-400/20 text-lime-400 rounded-xl border border-lime-400/30 flex items-center">
              <TrendingUp className="w-3.5 h-3.5 mr-1" />
              +14% PPG Avg
            </span>
          </div>

          <div className="h-64 w-full pt-2">
            <ResponsiveContainer width="100%" height="100%">
              {selectedSport === 'basketball' ? (
                <LineChart data={basketballGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="pts" name="Points (PTS)" stroke="#A3E635" strokeWidth={3} dot={{ r: 5 }} />
                  <Line type="monotone" dataKey="ast" name="Assists (AST)" stroke="#F43F5E" strokeWidth={2} />
                  <Line type="monotone" dataKey="reb" name="Rebounds (REB)" stroke="#38BDF8" strokeWidth={2} />
                </LineChart>
              ) : selectedSport === 'pickleball' ? (
                <BarChart data={pickleballGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="dinks" name="Kitchen Dinks" fill="#A3E635" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="aces" name="Aces Served" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : selectedSport === 'softball' ? (
                <BarChart data={softballGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="hits" name="Base Hits" fill="#A3E635" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="rbis" name="RBIs" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="stolenBases" name="Stolen Bases" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : selectedSport === 'football' ? (
                <BarChart data={footballGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="passingYards" name="Passing Yards" fill="#A3E635" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="touchdowns" name="Touchdowns (TD)" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : selectedSport === 'volleyball' ? (
                <BarChart data={volleyballGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="kills" name="Spike Kills" fill="#A3E635" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="digs" name="Ground Digs" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="aces" name="Service Aces" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="blocks" name="Net Blocks" fill="#FBBF24" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : selectedSport === 'tennis' ? (
                <BarChart data={tennisGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="aces" name="Service Aces" fill="#A3E635" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="breakPointsWon" name="Break Points Won" fill="#38BDF8" radius={[6, 6, 0, 0]} />
                </BarChart>
              ) : (
                <BarChart data={baseballGameLogs}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                  <XAxis dataKey="game" stroke="#A5B4FC" fontSize={12} />
                  <YAxis stroke="#A5B4FC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px', color: '#fff' }}
                  />
                  <Legend />
                  <Bar dataKey="hits" name="Base Hits" fill="#A3E635" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="rbis" name="RBIs" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>

        {/* Win-Loss Rate Donut Chart */}
        <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl flex flex-col justify-between space-y-4">
          <div>
            <h3 className="font-black italic uppercase text-white text-base">
              Overall Win / Loss Record
            </h3>
            <p className="text-xs text-indigo-200/70">
              League & Playground game outcomes
            </p>
          </div>

          <div className="h-48 w-full flex items-center justify-center relative">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={winLossData}
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {winLossData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <span className="text-3xl font-black italic text-lime-400">
                {winRatePercentage}%
              </span>
              <span className="text-[10px] text-indigo-200/70 font-black uppercase tracking-wider">
                Win Rate
              </span>
            </div>
          </div>

          <div className="flex items-center justify-around border-t border-white/10 pt-3 text-xs font-black uppercase">
            <div className="text-lime-400 flex items-center space-x-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-lime-400 inline-block shadow-sm shadow-lime-400" />
              <span>{user.winCount} Wins</span>
            </div>
            <div className="text-rose-400 flex items-center space-x-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block shadow-sm shadow-rose-500" />
              <span>{user.lossCount} Losses</span>
            </div>
          </div>
        </div>
      </div>

      {/* DEDICATED VOLLEYBALL PERFORMANCE CHART: KILLS VS ERRORS (LAST 20 MATCHES) */}
      <div className="bg-gradient-to-br from-indigo-900/90 via-purple-950/80 to-indigo-950 p-6 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg">
              <Activity className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-lime-400/20 text-lime-300 rounded font-mono border border-lime-400/30">
                  VOLLEYBALL TELEMETRY ANALYTICS
                </span>
                <span className="text-xs text-indigo-200/80 font-bold">20-Match Trend</span>
              </div>
              <h3 className="text-xl font-black italic uppercase text-white mt-1">
                Volleyball Performance: Kills vs. Attack Errors
              </h3>
              <p className="text-xs text-indigo-200/70 mt-0.5">
                Tracking offensive efficiency (Spike Kills) against costly Errors over your last 20 volleyball games.
              </p>
            </div>
          </div>

          {/* Toggle League Average Comparison */}
          <div className="flex items-center space-x-3 bg-indigo-950/90 p-2 rounded-2xl border border-white/10 shrink-0">
            <span className="text-xs font-bold text-indigo-200 pl-1">Compare League Avg:</span>
            <button
              type="button"
              onClick={() => setCompareVbLeagueAvg(!compareVbLeagueAvg)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-black italic uppercase transition flex items-center space-x-1.5 ${
                compareVbLeagueAvg
                  ? 'bg-lime-400 text-black shadow-lg shadow-lime-400/20'
                  : 'bg-indigo-900 text-indigo-300 border border-white/10 hover:text-white'
              }`}
            >
              <span>{compareVbLeagueAvg ? 'ON 📊' : 'OFF 🙈'}</span>
            </button>
          </div>
        </div>

        {/* Efficiency Metric Summary Pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.05 }}
            className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 text-center shadow-lg"
          >
            <span className="text-[10px] text-indigo-300 uppercase font-black block">20-Match Avg Kills</span>
            <p className="text-2xl font-black text-lime-400 italic mt-0.5">
              <AnimatedCountUp value={16.8} decimals={1} />
            </p>
            <span className="text-[10px] text-lime-300 font-mono font-bold">+4.3 vs League Avg (12.5)</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 text-center shadow-lg"
          >
            <span className="text-[10px] text-indigo-300 uppercase font-black block">20-Match Avg Errors</span>
            <p className="text-2xl font-black text-rose-400 italic mt-0.5">
              <AnimatedCountUp value={3.1} decimals={1} />
            </p>
            <span className="text-[10px] text-emerald-400 font-mono font-bold">-1.1 vs League Avg (4.2)</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.15 }}
            className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 text-center shadow-lg"
          >
            <span className="text-[10px] text-indigo-300 uppercase font-black block">Kill-to-Error Efficiency</span>
            <p className="text-2xl font-black text-cyan-400 italic mt-0.5">
              <AnimatedCountUp value={5.42} decimals={2} suffix="x" />
            </p>
            <span className="text-[10px] text-cyan-300 font-mono font-bold">High Precision</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 text-center shadow-lg"
          >
            <span className="text-[10px] text-indigo-300 uppercase font-black block">Net Point Advantage</span>
            <p className="text-2xl font-black text-amber-300 italic mt-0.5">
              <AnimatedCountUp value={274} prefix="+" suffix=" PTS" />
            </p>
            <span className="text-[10px] text-amber-200 font-mono font-bold">20-Match Cumulative</span>
          </motion.div>
        </div>

        {/* Recharts Kills vs Errors 20-Match Chart */}
        <div className="h-80 w-full pt-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[
                { match: 'M1', kills: 12, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M2', kills: 15, errors: 2, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M3', kills: 10, errors: 5, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M4', kills: 18, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M5', kills: 14, errors: 4, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M6', kills: 16, errors: 2, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M7', kills: 11, errors: 6, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M8', kills: 19, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M9', kills: 17, errors: 1, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M10', kills: 21, errors: 4, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M11', kills: 13, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M12', kills: 20, errors: 2, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M13', kills: 15, errors: 5, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M14', kills: 17, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M15', kills: 22, errors: 2, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M16', kills: 14, errors: 4, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M17', kills: 19, errors: 2, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M18', kills: 16, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M19', kills: 23, errors: 1, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
                { match: 'M20', kills: 18, errors: 3, leagueAvgKills: 12.5, leagueAvgErrors: 4.2 },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis dataKey="match" stroke="#A5B4FC" fontSize={11} />
              <YAxis stroke="#A5B4FC" fontSize={11} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0B0F2A',
                  borderColor: '#312E81',
                  borderRadius: '16px',
                  color: '#fff',
                }}
              />
              <Legend />
              <Bar dataKey="kills" name="Spike Kills (User)" fill="#A3E635" radius={[4, 4, 0, 0]} />
              <Bar dataKey="errors" name="Attack Errors (User)" fill="#F43F5E" radius={[4, 4, 0, 0]} />

              {/* Conditional League Average Overlays */}
              {compareVbLeagueAvg && (
                <>
                  <Line
                    type="monotone"
                    dataKey="leagueAvgKills"
                    name="League Avg Kills (12.5)"
                    stroke="#FBBF24"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="leagueAvgErrors"
                    name="League Avg Errors (4.2)"
                    stroke="#A855F7"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    dot={false}
                  />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* VOLLEYBALL SKILL DISTRIBUTION RADAR CHART (KILLS, ACES, BLOCKS, DIGS) WITH WEEKLY / MONTHLY TREND TOGGLE */}
      <div className="bg-gradient-to-br from-indigo-950 via-purple-950 to-indigo-900 p-6 rounded-[2.5rem] border-2 border-lime-400/40 shadow-2xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg">
              <Target className="w-6 h-6 stroke-[2.5]" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 font-mono font-black text-[10px] uppercase rounded border border-lime-400/30">
                  VOLLEYBALL SKILL RADAR
                </span>
                <span className="text-xs text-indigo-200/80 font-bold">Recharts Distribution</span>
              </div>
              <h3 className="text-xl font-black italic uppercase text-white mt-1">
                Volleyball Skill Distribution (Kills, Aces, Blocks, Digs)
              </h3>
              <p className="text-xs text-indigo-200/70 mt-0.5">
                Visualizing skill balance across offensive spiking, service pressure, net wall defense, and backcourt floor digs.
              </p>
            </div>
          </div>

          {/* Timeframe Toggle Button */}
          <div className="flex items-center space-x-2 bg-indigo-950/90 p-1.5 rounded-2xl border border-white/10 shrink-0 font-mono">
            <span className="text-xs font-bold text-indigo-200 px-2">Trend View:</span>
            <button
              type="button"
              onClick={() => setVbRadarTimeframe('weekly')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                vbRadarTimeframe === 'weekly'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Weekly Trend 📅
            </button>
            <button
              type="button"
              onClick={() => setVbRadarTimeframe('monthly')}
              className={`px-3 py-1.5 rounded-xl text-xs font-black italic uppercase transition ${
                vbRadarTimeframe === 'monthly'
                  ? 'bg-lime-400 text-black shadow-md'
                  : 'text-indigo-200/60 hover:text-white'
              }`}
            >
              Monthly Trend 🗓️
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          {/* Recharts Radar Chart Container */}
          <div className="md:col-span-7 h-72 w-full flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart
                cx="50%"
                cy="50%"
                outerRadius="75%"
                data={[
                  {
                    skill: 'Kills 💥',
                    value: vbRadarTimeframe === 'weekly'
                      ? Math.min(100, Math.round(((user.stats?.volleyball?.kills || 34) / 40) * 100))
                      : Math.min(100, Math.round(((user.stats?.volleyball?.kills || 34) / 30) * 100)),
                  },
                  {
                    skill: 'Aces ⚡',
                    value: vbRadarTimeframe === 'weekly'
                      ? Math.min(100, Math.round(((user.stats?.volleyball?.aces || 12) / 20) * 100))
                      : Math.min(100, Math.round(((user.stats?.volleyball?.aces || 12) / 15) * 100)),
                  },
                  {
                    skill: 'Blocks 🧱',
                    value: vbRadarTimeframe === 'weekly'
                      ? Math.min(100, Math.round(((user.stats?.volleyball?.blocks || 15) / 20) * 100))
                      : Math.min(100, Math.round(((user.stats?.volleyball?.blocks || 15) / 15) * 100)),
                  },
                  {
                    skill: 'Digs 🛡️',
                    value: vbRadarTimeframe === 'weekly'
                      ? Math.min(100, Math.round(((user.stats?.volleyball?.digs || 28) / 50) * 100))
                      : Math.min(100, Math.round(((user.stats?.volleyball?.digs || 28) / 35) * 100)),
                  },
                ]}
              >
                <PolarGrid stroke="#3730A3" />
                <PolarAngleAxis dataKey="skill" stroke="#E0E7FF" tick={{ fill: '#A3E635', fontSize: 12, fontWeight: 'bold' }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#6366F1" />
                <Radar
                  name="Volleyball Attributes"
                  dataKey="value"
                  stroke="#A3E635"
                  fill="#A3E635"
                  fillOpacity={0.4}
                />
                <Tooltip
                  contentStyle={{ backgroundColor: '#0F172A', borderColor: '#A3E635', borderRadius: '0.75rem', color: '#FFF' }}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown stat pills */}
          <div className="md:col-span-5 space-y-3">
            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 flex items-center justify-between">
              <span className="text-xs font-extrabold text-white flex items-center space-x-2">
                <span>💥 Kills (Attack Spikes)</span>
              </span>
              <span className="text-sm font-black font-mono text-lime-400">
                {user.stats?.volleyball?.kills || 34} Total
              </span>
            </div>

            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 flex items-center justify-between">
              <span className="text-xs font-extrabold text-white flex items-center space-x-2">
                <span>⚡ Service Aces</span>
              </span>
              <span className="text-sm font-black font-mono text-amber-300">
                {user.stats?.volleyball?.aces || 12} Total
              </span>
            </div>

            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 flex items-center justify-between">
              <span className="text-xs font-extrabold text-white flex items-center space-x-2">
                <span>🧱 Net Stuff Blocks</span>
              </span>
              <span className="text-sm font-black font-mono text-cyan-300">
                {user.stats?.volleyball?.blocks || 15} Total
              </span>
            </div>

            <div className="p-3.5 bg-indigo-950/80 rounded-2xl border border-white/10 flex items-center justify-between">
              <span className="text-xs font-extrabold text-white flex items-center space-x-2">
                <span>🛡️ Backcourt Floor Digs</span>
              </span>
              <span className="text-sm font-black font-mono text-emerald-400">
                {user.stats?.volleyball?.digs || 28} Total
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: Athlete Head-to-Head Comparison Tool */}
      <div className="bg-indigo-900/60 p-6 rounded-[2.5rem] border border-white/10 shadow-xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center space-x-2">
              <Users className="w-5 h-5 text-rose-400" />
              <h3 className="font-black italic uppercase text-white text-lg">
                Head-to-Head Athlete Skill Radar
              </h3>
            </div>
            <p className="text-xs text-indigo-200/70 font-medium">
              Compare your attribute profile with other league standouts.
            </p>
          </div>

          {/* Select comparison athlete */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-black uppercase text-indigo-200/80">
              Compare against:
            </span>
            <select
              value={compareAthleteId}
              onChange={(e) => setCompareAthleteId(e.target.value)}
              className="bg-indigo-950 text-white text-xs font-bold px-3 py-2 rounded-xl border border-white/10 outline-none focus:ring-2 focus:ring-lime-400"
            >
              {athletes.filter((a) => a.id !== user.id).map((athlete) => (
                <option key={athlete.id} value={athlete.id}>
                  {athlete.name} ({athlete.schoolOrLeague.split('/')[0]})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Radar & Head-to-Head Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={basketballRadarData}>
                <PolarGrid stroke="#312E81" opacity={0.4} />
                <PolarAngleAxis dataKey="subject" stroke="#A5B4FC" fontSize={11} />
                <PolarRadiusAxis angle={30} domain={[0, 'auto']} stroke="#A5B4FC" fontSize={10} />
                <Radar name={user?.name || 'Athlete'} dataKey="A" stroke="#A3E635" fill="#A3E635" fillOpacity={0.4} />
                <Radar name={compareAthlete?.name || 'Compare'} dataKey="B" stroke="#F43F5E" fill="#F43F5E" fillOpacity={0.4} />
                <Tooltip contentStyle={{ backgroundColor: '#0B0F2A', borderColor: '#312E81', borderRadius: '16px' }} />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-4 bg-indigo-950/80 p-5 rounded-2xl border border-white/10">
            <h4 className="font-black italic uppercase text-xs text-lime-400 border-b border-white/10 pb-2">
              Matchup Overview
            </h4>

            <div className="grid grid-cols-3 text-center text-xs font-black uppercase border-b border-white/10 pb-2">
              <span className="text-lime-400">{(user?.name || 'Athlete').split(' ')[0]}</span>
              <span className="text-indigo-300/60">Metric</span>
              <span className="text-rose-400">{(compareAthlete?.name || 'Compare').split(' ')[0]}</span>
            </div>

            <div className="space-y-2 text-xs font-bold">
              <div className="grid grid-cols-3 text-center">
                <span className="text-lime-400 font-black italic">
                  {bGamesA > 0 ? (user.stats.basketball.pts / bGamesA).toFixed(1) : '0.0'}
                </span>
                <span className="text-indigo-200/70 font-medium">Points Per Game</span>
                <span className="text-rose-400 font-black italic">
                  {bGamesB > 0 ? (compareAthlete.stats.basketball.pts / bGamesB).toFixed(1) : '0.0'}
                </span>
              </div>
              <div className="grid grid-cols-3 text-center">
                <span className="text-lime-400 font-black italic">
                  {bGamesA > 0 ? (user.stats.basketball.ast / bGamesA).toFixed(1) : '0.0'}
                </span>
                <span className="text-indigo-200/70 font-medium">Assists Per Game</span>
                <span className="text-rose-400 font-black italic">
                  {bGamesB > 0 ? (compareAthlete.stats.basketball.ast / bGamesB).toFixed(1) : '0.0'}
                </span>
              </div>
              <div className="grid grid-cols-3 text-center">
                <span className="text-lime-400 font-black italic">
                  {bFgAttA > 0 ? Math.round((user.stats.basketball.fgMade / bFgAttA) * 100) : 0}%
                </span>
                <span className="text-indigo-200/70 font-medium">Field Goal %</span>
                <span className="text-rose-400 font-black italic">
                  {bFgAttB > 0 ? Math.round((compareAthlete.stats.basketball.fgMade / bFgAttB) * 100) : 0}%
                </span>
              </div>
              <div className="grid grid-cols-3 text-center">
                <span className="text-lime-400 font-black italic">{user.level}</span>
                <span className="text-indigo-200/70 font-medium">Athlete Level</span>
                <span className="text-rose-400 font-black italic">{compareAthlete.level}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Historical Badge Pin Modal */}
      {selectedBadgePin && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
          <div className="bg-gradient-to-b from-indigo-950 via-slate-900 to-indigo-950 border-2 border-amber-400/80 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4 relative">
            <button
              type="button"
              onClick={() => setSelectedBadgePin(null)}
              className="absolute top-4 right-4 p-2 text-indigo-300 hover:text-white bg-indigo-900/60 hover:bg-indigo-800 rounded-full transition"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="text-center space-y-2 pt-2">
              <div className="w-20 h-20 mx-auto rounded-3xl bg-amber-400/20 border-2 border-amber-400 flex items-center justify-center text-4xl shadow-xl shadow-amber-400/20 animate-bounce">
                {selectedBadgePin.badgeIcon}
              </div>
              <div className="inline-block px-3 py-0.5 rounded-full bg-amber-400 text-black text-[10px] font-black uppercase font-mono tracking-wider">
                {selectedBadgePin.tier} • HISTORICAL UNLOCK 🏆
              </div>
              <h3 className="text-2xl font-black italic uppercase text-white tracking-tight">
                {selectedBadgePin.badgeName}
              </h3>
              <p className="text-xs text-indigo-200/80 font-mono">
                Unlocked on <strong className="text-amber-300">{selectedBadgePin.match}</strong> ({selectedBadgePin.unlockedAt})
              </p>
            </div>

            <div className="p-4 bg-indigo-900/60 rounded-2xl border border-white/10 space-y-2 text-xs">
              <div className="flex items-center justify-between text-indigo-200">
                <span className="font-bold">Match Stat Milestone:</span>
                <span className="font-mono font-black text-amber-300">{selectedBadgePin.statHighlight}</span>
              </div>
              <div className="flex items-center justify-between text-indigo-200">
                <span className="font-bold">XP Bonus Earned:</span>
                <span className="font-mono font-black text-lime-400 bg-lime-950 px-2 py-0.5 rounded border border-lime-400/30">
                  +{selectedBadgePin.xpReward} XP ⚡
                </span>
              </div>
              <p className="text-indigo-200/90 leading-relaxed pt-1 border-t border-white/10">
                {selectedBadgePin.description}
              </p>
            </div>

            <button
              type="button"
              onClick={() => setSelectedBadgePin(null)}
              className="w-full py-3 bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-black font-black italic uppercase text-sm rounded-xl shadow-lg transition"
            >
              Close Badge Historical View
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const ShotZoneSuccessChart: React.FC<{ user: AthleteProfile; onOpenProCheckout?: (contextMessage?: string) => void }> = ({ user, onOpenProCheckout }) => {
  const [viewMode, setViewMode] = useState<'my_shots' | 'league_avg' | 'comparison'>('comparison');

  const pts = user.stats?.basketball?.pts || 120;
  const games = user.stats?.basketball?.gamesPlayed || 10;
  const ppg = pts / games;

  const zoneData = [
    { zone: 'Paint / Rim', myFG: Math.min(85, Math.round(58 + ppg * 1.2)), myAttempts: 142, leagueFG: 58 },
    { zone: 'Mid-Left', myFG: Math.min(65, Math.round(42 + ppg * 0.5)), myAttempts: 65, leagueFG: 41 },
    { zone: 'Mid-Center', myFG: Math.min(70, Math.round(45 + ppg * 0.8)), myAttempts: 88, leagueFG: 43 },
    { zone: 'Mid-Right', myFG: Math.min(62, Math.round(40 + ppg * 0.6)), myAttempts: 58, leagueFG: 40 },
    { zone: 'Corner 3 (L)', myFG: Math.min(58, Math.round(38 + ppg * 0.5)), myAttempts: 42, leagueFG: 37 },
    { zone: 'Top 3-PT', myFG: Math.min(55, Math.round(35 + ppg * 0.7)), myAttempts: 95, leagueFG: 34 },
    { zone: 'Corner 3 (R)', myFG: Math.min(60, Math.round(39 + ppg * 0.6)), myAttempts: 48, leagueFG: 36 },
  ];

  return (
    <ProRestrictedWrapper
      user={user}
      onOpenProCheckout={onOpenProCheckout}
      featureTitle="Shot Zone Precision & Court Hotspots"
      featureDescription="Unlock D3 court spatial mapping and zone efficiency breakdowns against league benchmarks."
      upsellMessage="This historical performance deep-dive requires a PRO membership to unlock."
    >
      <div className="bg-gradient-to-br from-indigo-900/90 via-indigo-950 to-amber-950/40 p-6 rounded-[2.5rem] border-2 border-amber-400/50 shadow-2xl space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 border-b border-white/10 pb-4">
        <div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-0.5 bg-amber-400 text-black text-[10px] font-black uppercase font-mono rounded">
              PRO ANALYTICS TELEMETRY 🎯
            </span>
            <span className="text-xs text-amber-300 font-bold">Stored Court Location Data (`statsHistory`)</span>
          </div>
          <h3 className="text-xl font-black italic uppercase text-white mt-1 flex items-center space-x-2">
            <span>Shot Success Rate by Court Zone</span>
            <Sparkles className="w-5 h-5 text-amber-400" />
          </h3>
        </div>

        {/* View Mode Toggle Buttons */}
        <div className="flex items-center bg-indigo-950 p-1.5 rounded-xl border border-white/10 gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setViewMode('my_shots')}
            className={`px-3 py-1.5 rounded-lg text-xs font-black italic uppercase transition ${
              viewMode === 'my_shots' ? 'bg-amber-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
            }`}
          >
            My Shot Distribution
          </button>
          <button
            type="button"
            onClick={() => setViewMode('league_avg')}
            className={`px-3 py-1.5 rounded-lg text-xs font-black italic uppercase transition ${
              viewMode === 'league_avg' ? 'bg-amber-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
            }`}
          >
            League Average
          </button>
          <button
            type="button"
            onClick={() => setViewMode('comparison')}
            className={`px-3 py-1.5 rounded-lg text-xs font-black italic uppercase transition ${
              viewMode === 'comparison' ? 'bg-amber-400 text-black shadow-md' : 'text-indigo-300 hover:text-white'
            }`}
          >
            Side-by-Side Comparison
          </button>
        </div>
      </div>

      {/* Court Zone Graphic Map & Recharts Bar Overlay */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
        {/* Court Spatial Map SVG representation */}
        <div className="bg-indigo-950/80 p-4 rounded-2xl border border-amber-400/30 flex flex-col items-center justify-center space-y-3 relative overflow-hidden">
          <div className="text-[10px] text-amber-300 font-mono font-bold uppercase tracking-wider">
            Half-Court Zone Spatial Overlay
          </div>
          <div className="w-full max-w-[240px] aspect-[4/3] bg-amber-950/40 rounded-xl border-2 border-amber-400/50 p-2 relative flex flex-col justify-between">
            {/* Top 3-pt Arc */}
            <div className="absolute top-2 left-1/2 -translate-x-1/2 w-40 h-20 border-b-2 border-dashed border-amber-400/60 rounded-b-full flex items-center justify-center">
              <span className="text-[9px] font-black text-amber-300 bg-black/60 px-1 rounded">Top 3PT ({zoneData[5].myFG}%)</span>
            </div>
            {/* Paint / Key */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-16 bg-amber-400/20 border-t-2 border-x-2 border-amber-400 rounded-t-lg flex items-center justify-center">
              <span className="text-[9px] font-black text-lime-400 bg-black/70 px-1 rounded">Paint ({zoneData[0].myFG}%)</span>
            </div>
            {/* Corners */}
            <div className="absolute bottom-1 left-2 text-[8px] font-mono text-amber-300 bg-indigo-950 px-1 rounded border border-amber-400/30">
              LC 3 ({zoneData[4].myFG}%)
            </div>
            <div className="absolute bottom-1 right-2 text-[8px] font-mono text-amber-300 bg-indigo-950 px-1 rounded border border-amber-400/30">
              RC 3 ({zoneData[6].myFG}%)
            </div>
          </div>
          <div className="text-[10px] text-indigo-200/70 text-center font-mono">
            Spatial precision mapped from {user.statsHistory?.length || 12} logged game sessions.
          </div>
        </div>

        {/* Recharts Bar Chart */}
        <div className="lg:col-span-2 h-72 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={zoneData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#312E81" opacity={0.3} />
              <XAxis dataKey="zone" stroke="#A5B4FC" fontSize={11} />
              <YAxis stroke="#A5B4FC" fontSize={11} unit="%" domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0B0F2A',
                  borderColor: '#F59E0B',
                  borderRadius: '16px',
                  color: '#fff',
                }}
                formatter={(val: any) => [`${val}% FG`, 'Efficiency']}
              />
              <Legend />
              {(viewMode === 'my_shots' || viewMode === 'comparison') && (
                <Bar dataKey="myFG" name="My Zone FG%" fill="#F59E0B" radius={[6, 6, 0, 0]} />
              )}
              {(viewMode === 'league_avg' || viewMode === 'comparison') && (
                <Bar dataKey="leagueFG" name="League Avg FG%" fill="#94A3B8" radius={[6, 6, 0, 0]} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Hotspots summary banner */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-white/10 text-center font-mono">
        <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-amber-400/30">
          <span className="text-[9px] text-indigo-300 uppercase block">High-Efficiency Hotspot</span>
          <span className="text-xs font-bold text-amber-300">Paint / Restricted ({zoneData[0].myFG}% FG)</span>
        </div>
        <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-amber-400/30">
          <span className="text-[9px] text-indigo-300 uppercase block">Top Perimeter Zone</span>
          <span className="text-xs font-bold text-lime-400">Right Corner 3 (+24% Over Avg)</span>
        </div>
        <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-amber-400/30">
          <span className="text-[9px] text-indigo-300 uppercase block">Mid-Range Precision</span>
          <span className="text-xs font-bold text-amber-300">High Key Mid-Range ({zoneData[2].myFG}%)</span>
        </div>
        <div className="p-2.5 bg-indigo-950/80 rounded-xl border border-amber-400/30">
          <span className="text-[9px] text-indigo-300 uppercase block">Scout Growth Target</span>
          <span className="text-xs font-bold text-amber-400">Left Wing Pull-Up Jumpers</span>
        </div>
      </div>
    </div>
    </ProRestrictedWrapper>
  );
};
